/**
 * UpgradeProModal - Modal réutilisable pour conversion Premium (P2.3)
 * 
 * Affiche la valeur Pro de manière contextuelle selon l'action déclencheuse.
 * Non-bloquant, fermable, pas redondant (1 affichage / session).
 */

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Check, Sparkles, FileDown, Palette, Layers, Zap, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useBeta } from '../hooks/useBeta';

// P2.3: Track modal opens per session (prevent redundancy)
const MODAL_SESSION_KEY = 'upgrade_pro_modal_shown';

const UpgradeProModal = ({ 
  isOpen, 
  onClose, 
  context = 'general' // 'export', 'variant', 'branding', 'generator', 'general'
}) => {
  const navigate = useNavigate();
  const beta = useBeta();
  const [hasBeenShown, setHasBeenShown] = useState(false);

  // P2.3: Check if modal already shown this session
  useEffect(() => {
    if (isOpen && !hasBeenShown) {
      const shown = sessionStorage.getItem(MODAL_SESSION_KEY);
      if (shown) {
        setHasBeenShown(true);
        onClose(); // Auto-close if already shown
        return;
      }
      // Mark as shown
      sessionStorage.setItem(MODAL_SESSION_KEY, 'true');
      setHasBeenShown(true);
      
      // P2.3: Track event
      trackPremiumEvent('upgrade_modal_opened', { context });
    }
  }, [isOpen, hasBeenShown, context, onClose]);

  const handleStartTrial = () => {
    trackPremiumEvent('upgrade_converted', { context });
    onClose();
    navigate('/pricing');
  };

  // P2.3: Contextual benefits based on trigger
  // P1.7: filtres par clé stable + wording école (voir WORDING_SIMPLIFIE.md)
  const getContextualBenefits = () => {
    const allBenefits = [
      {
        key: 'export',
        icon: <FileDown className="h-5 w-5 text-primary" />,
        iconBg: "bg-accent",
        title: "Téléchargements PDF illimités",
        description: "Téléchargez autant de fiches que nécessaire, sans limite quotidienne"
      },
      {
        key: 'variant',
        icon: <Layers className="h-5 w-5 text-purple-600" />,
        iconBg: "bg-purple-100",
        title: "Versions A/B/C (anti-copiage)",
        description: "Plusieurs versions d'une même fiche pour vos évaluations"
      },
      {
        key: 'branding',
        icon: <Palette className="h-5 w-5 text-green-600" />,
        iconBg: "bg-green-100",
        title: "Logo et en-tête personnalisés",
        description: "Vos fiches à vos couleurs : logo, nom, établissement"
      }
      // « Exercices Pro » et « Bibliothèque » avaient déjà été retirés :
      // aucun générateur n'est réservé au Premium et un compte gratuit
      // enregistre ses fiches sans limite. « Mise en page compacte » les
      // rejoint : son contrôle a disparu de l'interface, et les trois
      // densités sont ouvertes à tous. On ne vend que ce que le code livre.
    ];

    // Filter benefits based on context
    switch (context) {
      case 'export':
        return allBenefits.filter(b => ['export', 'branding'].includes(b.key));
      case 'variant':
        return allBenefits.filter(b => ['variant', 'branding'].includes(b.key));
      case 'branding':
        return allBenefits.filter(b => ['branding', 'export'].includes(b.key));
      case 'generator':
        return allBenefits.filter(b => ['variant', 'export'].includes(b.key));
      default:
        return allBenefits.slice(0, 4); // Show top 4 for general
    }
  };

  const benefits = getContextualBenefits();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="h-6 w-6 text-purple-600" />
            {beta.active ? 'Aller plus loin' : 'Aller plus loin avec Pro'}
          </DialogTitle>
          <DialogDescription className="text-base">
            {beta.active
              ? `Cette option est incluse dans la bêta fondateur, offerte pendant ${Math.round(beta.jours / 30)} mois. Voici ce qu'elle vous apporte :`
              : "Cette option fait partie de l'offre Pro. Voici ce qu'elle vous apporte :"}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className={`mt-1 p-2 ${benefit.iconBg} rounded-lg`}>
                {benefit.icon}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">
                  {benefit.title}
                </h4>
                <p className="text-sm text-gray-600">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={handleStartTrial}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
            size="lg"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {beta.active ? 'Découvrir la bêta fondateur' : 'Essayer Pro (7 jours)'}
          </Button>
          <Button
            onClick={onClose}
            variant="ghost"
            className="flex-1"
            size="lg"
          >
            Plus tard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// P2.3: Track premium events (instrumentation)
export const trackPremiumEvent = (eventName, data = {}) => {
  const eventData = {
    event: eventName,
    timestamp: new Date().toISOString(),
    ...data
  };
  
  // Log to console (ready for analytics integration later)
  console.log('📊 Premium Event:', eventData);
  
  // Store in localStorage for debugging
  try {
    const events = JSON.parse(localStorage.getItem('premium_events') || '[]');
    events.push(eventData);
    // Keep only last 50 events
    if (events.length > 50) {
      events.shift();
    }
    localStorage.setItem('premium_events', JSON.stringify(events));
  } catch (e) {
    console.error('Error storing premium event:', e);
  }
};

export default UpgradeProModal;







